import type {
  PrivateAgentTouchedEntityDirectory,
  PrivateAgentTurn,
  PrivateAgentTurnTouchedEntity
} from "@salesko/contracts";
import { Banner, Button, Spinner } from "@salesko/design-system";
import { useQuery } from "@tanstack/react-query";
import { PanelRight, Users } from "lucide-react";
import type { ReactNode } from "react";
import { useI18n } from "../../i18n";
import { entityLabel } from "../i18n";
import { privateAgentConversationQueryOptions } from "../hooks/usePrivateAgentChat";

export type PrivateAgentDetailTab = "detail" | "conversation";

/**
 * The panel speaks about people, companies and deals, so only those three
 * entity types are listed, in that order. An event or a note the assistant read
 * stays out of it rather than widening what the heading promises.
 */
const TouchedEntityTypes = ["person", "organisation", "opportunity"] as const;

type TouchedEntityType = (typeof TouchedEntityTypes)[number];

/**
 * One entity the caller can open. It carries the registry's latest frame and
 * node, so the route can open it from any frame instead of only from the one
 * currently loaded.
 */
export type PrivateAgentTouchedEntityRow = {
  canonicalEntityId: string;
  entityType: TouchedEntityType;
  label: string;
  latestFrameRecordId: string;
  latestNodeId: string;
};

type Copy = ReturnType<typeof useI18n>["t"];

type PrivateAgentDetailTabsProps = {
  activeTab: PrivateAgentDetailTab;
  agentId: string | undefined;
  conversationId: string | undefined;
  /** The `详情` surface, rendered by the route exactly as it is today. */
  detail: ReactNode;
  enabled: boolean;
  onSelectEntity: (entity: PrivateAgentTouchedEntityRow) => void;
  onTabChange: (tab: PrivateAgentDetailTab) => void;
};

/**
 * Every person, company and deal the loaded turns of one conversation read,
 * deduped by canonical entity id and ordered by first appearance. The
 * conversation read returns turns newest-first, so the walk runs backwards to
 * make "first" mean the earliest turn that touched the entity.
 *
 * This decides display order and the badge count only. The turn store is the
 * authority for which ids were touched; the server's directory is the authority
 * for what each id is called.
 */
export function privateAgentConversationTouchedEntities(
  turnsNewestFirst: readonly PrivateAgentTurn[]
): PrivateAgentTurnTouchedEntity[] {
  const listed: ReadonlySet<string> = new Set<string>(TouchedEntityTypes);
  const seen = new Set<string>();
  const entities: PrivateAgentTurnTouchedEntity[] = [];
  for (const turn of [...turnsNewestFirst].reverse()) {
    for (const entity of turn.touchedEntities) {
      if (!listed.has(entity.entityType)) continue;
      if (seen.has(entity.canonicalEntityId)) continue;
      seen.add(entity.canonicalEntityId);
      entities.push(entity);
    }
  }
  return entities;
}

/**
 * The Private Agent side panel: the shared entity detail and the entities this
 * conversation loaded, as two tabs over the one detail slot the workspace shell
 * owns. It subscribes to the same conversation query the chat column mounts, so
 * the query cache stays the single source and nothing is reported upward
 * through an effect.
 *
 * With no conversation selected there is no `本次对话` to offer, so the slot is
 * the plain detail surface it was before the tabs existed — a tab strip whose
 * second tab can only ever be empty is not a state worth rendering.
 */
export function PrivateAgentDetailTabs({
  activeTab,
  agentId,
  conversationId,
  detail,
  enabled,
  onSelectEntity,
  onTabChange
}: PrivateAgentDetailTabsProps) {
  const { t } = useI18n();
  const conversationQuery = useQuery(privateAgentConversationQueryOptions({ agentId, conversationId, enabled }));
  const touched = privateAgentConversationTouchedEntities(conversationQuery.data?.turns ?? []);

  if (!conversationId) {
    return <>{detail}</>;
  }

  return (
    <div className="private-agent-detail-tabs">
      <div
        aria-label={t.privateAgentWorkspace.detailTabsAriaLabel}
        className="private-agent-workspace-facets"
        role="tablist"
      >
        {(["detail", "conversation"] as const).map((candidate) => {
          const Icon = candidate === "detail" ? PanelRight : Users;
          const selected = activeTab === candidate;
          return (
            <button
              aria-controls={`private-agent-detail-${candidate}`}
              aria-selected={selected}
              className={selected ? "is-active" : undefined}
              id={`private-agent-detail-tab-${candidate}`}
              key={candidate}
              onClick={() => onTabChange(candidate)}
              role="tab"
              type="button"
            >
              <Icon aria-hidden="true" size={17} />
              {candidate === "detail" ? t.privateAgentWorkspace.detailTab : t.privateAgentWorkspace.conversationTab}
              {candidate === "conversation" && touched.length > 0 ? (
                <span className="private-agent-detail-tab-count">{touched.length}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        aria-labelledby="private-agent-detail-tab-detail"
        className="private-agent-detail-tabpanel"
        hidden={activeTab !== "detail"}
        id="private-agent-detail-detail"
        role="tabpanel"
      >
        {detail}
      </div>

      <div
        aria-labelledby="private-agent-detail-tab-conversation"
        className="private-agent-detail-tabpanel"
        hidden={activeTab !== "conversation"}
        id="private-agent-detail-conversation"
        role="tabpanel"
      >
        <section aria-label={t.privateAgentWorkspace.contextAriaLabel} className="private-agent-touched-entities">
          <p className="private-agent-rail-heading">{t.privateAgentWorkspace.touchedEntitiesTitle}</p>
          <TouchedEntityList
            directory={conversationQuery.data?.touchedEntityDirectory}
            entities={touched}
            loading={conversationQuery.isLoading}
            onRetry={() => {
              void conversationQuery.refetch();
            }}
            onSelectEntity={onSelectEntity}
            t={t}
          />
        </section>
      </div>
    </div>
  );
}

function TouchedEntityList({
  directory,
  entities,
  loading,
  onRetry,
  onSelectEntity,
  t
}: {
  directory: PrivateAgentTouchedEntityDirectory | undefined;
  entities: readonly PrivateAgentTurnTouchedEntity[];
  loading: boolean;
  onRetry: () => void;
  onSelectEntity: (entity: PrivateAgentTouchedEntityRow) => void;
  t: Copy;
}) {
  if (loading) return <Spinner label={t.common.loading} size={16} />;

  // The registry read failed. No name is guessed from the loaded frame to fill
  // the gap: the read is offered again instead.
  if (directory?.status === "error") {
    return (
      <Banner action={<Button onClick={onRetry} size="sm" variant="secondary">{t.common.retry}</Button>} status="error">
        {t.privateAgentWorkspace.touchedEntitiesError}
      </Banner>
    );
  }

  if (!directory || entities.length === 0) {
    return <p className="private-agent-rail-empty">{t.privateAgentWorkspace.touchedEntitiesEmpty}</p>;
  }

  return (
    <>
      {TouchedEntityTypes.map((entityType) => {
        const group = entities.filter((entity) => entity.entityType === entityType);
        if (group.length === 0) return null;
        return (
          <div className="private-agent-touched-group" key={entityType}>
            <p className="private-agent-touched-group-heading">{entityLabel(t, entityType)}</p>
            <ul className="private-agent-context-list">
              {group.map((entity) => {
                const entry = directory.entries[entity.canonicalEntityId];
                // The registry answered and holds nothing for this id, so the
                // row shows the id it does have and stays inert.
                if (entry?.status !== "resolved") {
                  return (
                    <li className="private-agent-context-entity" key={entity.canonicalEntityId}>
                      <span>
                        <strong>{entity.canonicalEntityId}</strong>
                        <small>{t.privateAgentWorkspace.touchedEntityUnavailable}</small>
                      </span>
                    </li>
                  );
                }
                return (
                  <li className="private-agent-context-entity" key={entity.canonicalEntityId}>
                    <button
                      aria-label={t.privateAgentWorkspace.openEntityInGraph(entry.label)}
                      onClick={() =>
                        onSelectEntity({
                          canonicalEntityId: entity.canonicalEntityId,
                          entityType,
                          label: entry.label,
                          latestFrameRecordId: entry.latestFrameRecordId,
                          latestNodeId: entry.latestNodeId
                        })
                      }
                      type="button"
                    >
                      <strong>{entry.label}</strong>
                      <small>{entityLabel(t, entityType)}</small>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </>
  );
}
